import { createRef } from 'react';
import { createRoot, Root } from 'react-dom/client';
import { ChartExportRenderer } from 'shared/chart/core/types';

export const SvgMimeInfo = 'image/svg+xml;charset=utf-8';

export const renderToSvgElement = (
    renderChart: ChartExportRenderer,
    chartWidth: number,
    chartHeight: number,
) =>
    new Promise<SVGSVGElement>((resolve) => {
        const svgRef = createRef<SVGSVGElement>();

        const rootEl = document.createElement('div');
        rootEl.style.position = 'static';
        rootEl.style.left = '0';
        rootEl.style.top = '0';
        rootEl.style.visibility = 'hidden';
        rootEl.style.width = '0';
        rootEl.style.height = '0';
        rootEl.style.overflow = 'hidden';

        let root: Root | null = null;
        try {
            document.body.append(rootEl);
            root = createRoot(rootEl);
            root.render(<>{renderChart({ chartWidth, chartHeight, ref: svgRef })}</>);

            setTimeout(() => {
                const svgElement = svgRef.current;
                if (!svgElement) {
                    throw new Error('Unable to render chart SVG');
                }

                resolve(svgRef.current);
                root?.unmount();
            });
        } catch (e) {
            // eslint-disable-next-line no-console
            console.error(e);
            root?.unmount();
            rootEl.remove();
            throw e;
        }
    });

export const generateSvgXML = (svgElement: SVGSVGElement): string => {
    const preface = '<?xml version="1.0" encoding="utf-8"?>';
    const svgXml = new XMLSerializer().serializeToString(svgElement);
    return `${preface}\r\n${svgXml}`;
};

export const generateSvgBlob = (svgElement: SVGSVGElement): Blob => {
    const content = generateSvgXML(svgElement);
    return new Blob([content], { type: SvgMimeInfo });
};

export const generateSvgFile = (svgElement: SVGSVGElement, filename: string): File => {
    const clonedSvgElement = svgElement.cloneNode(true) as SVGSVGElement;

    clonedSvgElement.removeAttribute('width');
    clonedSvgElement.removeAttribute('height');

    const svgBlob = generateSvgBlob(clonedSvgElement);

    return new File([svgBlob], filename);
};

export const generatePngFile = (
    svgElement: SVGSVGElement,
    filename: string,
    width: number,
    height: number,
): Promise<File> =>
    new Promise<File>((resolve) => {
        const svgBlob = generateSvgBlob(svgElement);

        const canvas = document.createElement('canvas');
        canvas.setAttribute('width', width.toString());
        canvas.setAttribute('height', height.toString());
        const ctx = canvas.getContext('2d');

        if (!ctx) {
            throw new Error('Unable to get canvas context');
        }

        const imgSrc = URL.createObjectURL(svgBlob);
        const img = new Image(1720, 720);

        img.onload = () => {
            ctx.drawImage(img, 0, 0);

            URL.revokeObjectURL(imgSrc);

            canvas.toBlob((pngBlob: Blob | null) => {
                if (!pngBlob) {
                    throw new Error('Unable to generate png blob');
                }

                const pngFile = new File([pngBlob], filename);
                resolve(pngFile);
            }, 'image/png');
        };

        img.src = imgSrc;
    });
